import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, RefreshCw } from 'lucide-react';
import GrowthTabNav from './GrowthTabNav';
import GrowthDateFilter from './GrowthDateFilter';
import { getGrowthCampaigns } from '../../Services/GrowthService';
import { GrowthCampaign } from '../../Types/GrowthType';

const headerClass =
  'px-5 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500';

const badge = (count: number) =>
  count > 0
    ? 'inline-flex rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-400'
    : 'inline-flex rounded-lg border border-slate-700 bg-slate-800/50 px-2.5 py-1 text-xs font-bold text-slate-500';

function GrowthCampaignsComponent() {
  const [data, setData] = useState<GrowthCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGrowthCampaigns({
        fromDate: appliedFrom || undefined,
        toDate: appliedTo || undefined,
      });
      if (response.success && Array.isArray(response.data)) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Failed to load growth campaigns', err);
    } finally {
      setLoading(false);
    }
  }, [appliedFrom, appliedTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <GrowthTabNav />

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
          <Megaphone size={22} />
        </span>
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Campaign Performance</h1>
          <p className="text-xs font-semibold text-slate-400 md:text-sm">
            Visitors &amp; conversions attributed to tracked campaigns
          </p>
        </div>
        <button
          onClick={fetchData}
          title="Refresh"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <GrowthDateFilter
          fromDate={fromDate}
          toDate={toDate}
          onApply={(f, t) => {
            setFromDate(f);
            setToDate(t);
            setAppliedFrom(f);
            setAppliedTo(t);
          }}
        />
      </div>

      {loading && data.length === 0 ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
          <RefreshCw className="h-10 w-10 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="w-full min-w-[900px] text-left">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-900/60">
                <th className={headerClass}>Campaign</th>
                <th className={headerClass}>Source / Medium</th>
                <th className={headerClass}>Visitors</th>
                <th className={headerClass}>Registration Started</th>
                <th className={headerClass}>Registered</th>
                <th className={headerClass}>Approved</th>
                <th className={headerClass}>Conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {data.map((campaign, idx) => (
                <motion.tr
                  key={`${campaign.name ?? campaign.id ?? 'campaign'}-${idx}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="transition hover:bg-slate-900/70"
                >
                  <td className="px-5 py-4 text-sm font-bold text-slate-100">
                    {campaign.name || '—'}
                    {campaign.id && (
                      <span className="ml-2 hidden truncate text-[10px] font-semibold text-slate-600 lg:inline">
                        {campaign.id.slice(0, 8)}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">
                    {campaign.source || '—'}
                    {campaign.medium && <span className="text-slate-600"> / {campaign.medium}</span>}
                  </td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-200">{campaign.visitors.toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">{campaign.registrationStarted.toLocaleString()}</td>
                  <td className="px-5 py-4 text-sm font-semibold text-slate-300">{campaign.registrationCompleted.toLocaleString()}</td>
                  <td className="px-5 py-4">
                    <span className={badge(campaign.approved)}>{campaign.approved.toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-4 text-sm font-black text-cyan-400">{campaign.conversionPct.toFixed(1)}%</td>
                </motion.tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-sm font-semibold text-slate-500">
                    No campaign data for this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GrowthCampaignsComponent;